import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n/lib';
import AnimateHeight from 'react-animate-height';
import IconLazy from '../../_helpers/Icon/IconLazy';
import FooterNewsletter from './FooterNewsletter';
import {
  FooterNavColumn,
  FooterNavItem,
  getFooterBenefits,
  getFooterColumns,
  getFooterVariant,
  getPrimaryColumn,
  PAYMENT_LOGOS,
  showFooterPayments,
  SOCIAL_LINKS,
} from './footerData';

const MOBILE_MAX = 760;

const Wrapper = styled.footer`
  width: 100%;
  color: ${({ theme }) => theme.color.text};
  font-size: ${rem(14)};
`;

const Inner = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 ${rem(35)};

  ${({ theme }) => theme.mediab.s450`
    padding: 0 ${rem(16)};
  `};
`;

/* ---------------------------------- Benefits --------------------------------- */

const BenefitsSection = styled.div`
  background: ${({ theme }) => theme.color.white};
`;

const BenefitsGrid = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${rem(16)} ${rem(24)};
  padding: ${rem(22)} 0 ${rem(26)};

  ${({ theme }) => theme.mediab.m760`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${rem(18)} ${rem(12)};
    padding: ${rem(16)} 0 ${rem(20)};
  `};
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
`;

/* The lazily-loaded Icon injects its base styles (24px, text fill) AFTER
   these — hence the && specificity bump on every styled(IconLazy) here. */
const BenefitIcon = styled(IconLazy)`
  && {
    width: ${rem(24)};
    height: ${rem(24)};
    flex-shrink: 0;
    fill: ${({ theme }) => theme.color.black};
  }
`;

const BenefitLabel = styled.span`
  font-size: ${rem(15)};
  line-height: 1.35;
  max-width: ${rem(150)};
`;

/* ----------------------------------- Links ----------------------------------- */

const MainSection = styled.div`
  background: ${({ theme }) => theme.color.gray83};
  padding-bottom: ${rem(16)};
`;

const NavGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr)) auto;
  gap: ${rem(24)};
  padding: ${rem(24)} 0 ${rem(12)};

  &.five {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  ${({ theme }) => theme.mediab.l1050`
    grid-template-columns: repeat(2, minmax(0, 1fr));

    &.five {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  `};

  ${({ theme }) => theme.mediab.m760`
    display: block;
    padding: ${rem(12)} 0 ${rem(8)};
  `};
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.mediab.m760`
    display: block;

    &.collapsible {
      border-top: 1px solid ${({ theme }) => theme.color.gray80};
    }

    &.primary {
      padding-bottom: ${rem(10)};
    }
  `};
`;

const ColumnTitle = styled.h3`
  font-size: ${rem(15)};
  font-weight: 700;
  margin: 0 0 ${rem(4)};
  color: ${({ theme }) => theme.color.text};
`;

const ColumnToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: 0;
  padding: ${rem(12)} 0;
  cursor: pointer;
  font-family: inherit;
  font-size: ${rem(15)};
  font-weight: 700;
  color: ${({ theme }) => theme.color.text};
  text-align: left;
`;

const ToggleChevron = styled(IconLazy)`
  && {
    width: ${rem(14)};
    height: ${rem(14)};
    flex-shrink: 0;
    fill: ${({ theme }) => theme.color.text};
    transform: rotate(-90deg);
    ${({ theme }) => theme.transition('transform')};
  }

  &&.open {
    transform: rotate(0deg);
  }
`;

const ColumnList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  ${({ theme }) => theme.mediab.m760`
    padding-bottom: ${rem(10)};
  `};
`;

const ColumnItem = styled.li`
  padding: ${rem(2)} 0;

  ${({ theme }) => theme.mediab.m760`
    padding: ${rem(4)} 0;
  `};
`;

const ColumnLink = styled.a`
  color: ${({ theme }) => theme.color.text};
  text-decoration: none;
  font-size: ${rem(14)};
  line-height: 1.5;

  &:hover {
    color: ${({ theme }) => theme.color.primary};
    text-decoration: underline;
  }
`;

const ColumnButton = styled.button`
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.color.text};
  font-size: ${rem(14)};
  line-height: 1.5;
  text-align: left;
  font-family: inherit;

  &:hover {
    color: ${({ theme }) => theme.color.primary};
    text-decoration: underline;
  }
`;

/* --------------------------------- Payments ---------------------------------- */

const PaymentsBlock = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: ${rem(6)};
  /* Three tiles per row on desktop, as in the design. */
  max-width: ${rem(3 * 70 + 2 * 6)};

  ${({ theme }) => theme.mediab.l1050`
    grid-column: 1 / -1;
    max-width: none;
  `};

  ${({ theme }) => theme.mediab.m760`
    padding: ${rem(12)} 0 ${rem(4)};
  `};
`;

const PaymentTile = styled.div`
  width: ${rem(70)};
  height: ${rem(32)};
  background: ${({ theme }) => theme.color.white};
  border-radius: ${rem(4)};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(3)} ${rem(6)};
  box-sizing: border-box;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

/* ---------------------------------- Bottom ----------------------------------- */

const BottomSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(24)};
  padding-top: ${rem(16)};

  ${({ theme }) => theme.mediab.m760`
    flex-direction: column;
    gap: ${rem(16)};
    padding-top: ${rem(10)};
  `};
`;

const CompanyBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};

  ${({ theme }) => theme.mediab.m760`
    order: 2;
    align-items: center;
    text-align: center;
  `};
`;

const CompanyIdentity = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${rem(4)} ${rem(12)};

  ${({ theme }) => theme.mediab.m760`
    flex-direction: column;
    align-items: center;
    gap: ${rem(2)};
  `};
`;

const CompanyName = styled.div`
  font-weight: 700;
  font-size: ${rem(14)};
`;

const CompanyAddress = styled.div`
  font-size: ${rem(14)};
`;

const CompanyContacts = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${rem(6)} ${rem(20)};

  ${({ theme }) => theme.mediab.m760`
    flex-direction: column;
    gap: ${rem(8)};
  `};
`;

const ContactItem = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${rem(8)};
  color: ${({ theme }) => theme.color.text};
  text-decoration: none;
  font-size: ${rem(14)};

  &:hover {
    color: ${({ theme }) => theme.color.primary};
  }
`;

const ContactIcon = styled(IconLazy)`
  && {
    width: ${rem(20)};
    height: ${rem(20)};
    fill: ${({ theme }) => theme.color.primary};
    flex-shrink: 0;
  }
`;

const ScrollTopButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${rem(8)};
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.color.text};
  font-size: ${rem(14)};
  font-family: inherit;

  &:hover {
    color: ${({ theme }) => theme.color.primary};
  }

  ${({ theme }) => theme.mediab.m760`
    order: 3;
    padding-bottom: ${rem(4)};
  `};
`;

const ChevronsUp = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
`;

const ChevronUp = styled(IconLazy)`
  && {
    width: ${rem(12)};
    height: ${rem(8)};
    fill: currentColor;
    transform: rotate(180deg);
  }

  && + && {
    margin-top: ${rem(-3)};
  }
`;

const RightBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem(20)};

  ${({ theme }) => theme.mediab.m760`
    order: 1;
    justify-content: center;
    flex-wrap: wrap;
  `};
`;

const SocialList = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(16)};
`;

const SocialItem = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: ${({ theme }) => theme.color.text};
  font-size: ${rem(12)};
  gap: ${rem(4)};

  &:hover {
    color: ${({ theme }) => theme.color.primary};
  }
`;

const SocialIcon = styled(IconLazy)`
  && {
    width: ${rem(38)};
    height: ${rem(38)};
    ${({ theme }) => theme.transition('fill')};
  }

  ${SocialItem}:hover && {
    fill: ${({ theme }) => theme.color.primary};
  }
`;

const SocialCount = styled.span`
  font-size: ${rem(12)};
  color: ${({ theme }) => theme.color.text};
`;

const SaecBadge = styled.a`
  display: inline-block;
  width: ${rem(110)};
  height: ${rem(46)};
  background: url('/images/saec.svg') no-repeat center center;
  background-size: contain;
  flex-shrink: 0;
`;

interface FooterNewProps {
  footerPhone?: string;
  footerMail?: string;
  setChatVisibility?: (e?: any) => void;
  user?: any;
  mainMenu?: any[];
}

interface FooterNewState {
  isMobile: boolean;
  openSections: { [key: string]: boolean };
}

class FooterNew extends React.PureComponent<FooterNewProps, FooterNewState> {
  public state: FooterNewState = {
    isMobile: false,
    openSections: {},
  };

  private mql: MediaQueryList | null = null;

  public componentDidMount() {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.mql = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);
      this.mql.addListener(this.onViewportChange);
      if (this.mql.matches) {
        this.setState({ isMobile: true });
      }
    }
  }

  public componentWillUnmount() {
    if (this.mql) {
      this.mql.removeListener(this.onViewportChange);
    }
  }

  private onViewportChange = (e: MediaQueryListEvent) => {
    this.setState({ isMobile: e.matches });
  };

  private toggleSection = (key: string) => {
    this.setState((prev) => ({
      openSections: { ...prev.openSections, [key]: !prev.openSections[key] },
    }));
  };

  private handleScrollTop = (e: React.MouseEvent) => {
    e.preventDefault();
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  private renderItems = (items: FooterNavItem[]) => {
    const { setChatVisibility } = this.props;
    return (
      <ColumnList>
        {items.map((item, i) => (
          <ColumnItem key={i}>
            {item.chat ? (
              <ColumnButton
                type="button"
                onClick={(e) => setChatVisibility && setChatVisibility(e)}
              >
                {__(item.name)}
              </ColumnButton>
            ) : (
              <ColumnLink href={item.link}>{__(item.name)}</ColumnLink>
            )}
          </ColumnItem>
        ))}
      </ColumnList>
    );
  };

  private renderColumn = (column: FooterNavColumn) => {
    const { isMobile, openSections } = this.state;
    const open = Boolean(openSections[column.key]);

    if (!isMobile) {
      return (
        <Column key={column.key}>
          <ColumnTitle>{__(column.title)}</ColumnTitle>
          {this.renderItems(column.items)}
        </Column>
      );
    }

    return (
      <Column key={column.key} className="collapsible">
        <ColumnToggle
          type="button"
          onClick={() => this.toggleSection(column.key)}
          aria-expanded={open}
        >
          {__(column.title)}
          <ToggleChevron icon="chevron" className={open ? 'open' : ''} />
        </ColumnToggle>
        <AnimateHeight duration={250} height={open ? 'auto' : 0}>
          {this.renderItems(column.items)}
        </AnimateHeight>
      </Column>
    );
  };

  public render() {
    const { user } = this.props;
    const footerPhone = this.props.footerPhone || '+421 46 519 87 11';
    const footerMail = this.props.footerMail || 'daffer@daffer.sk';

    const variant = getFooterVariant(user);
    const benefits = getFooterBenefits(variant);
    const columns = getFooterColumns(variant, this.props.mainMenu);
    const primaryItems = getPrimaryColumn(variant);
    const withPayments = showFooterPayments(variant);

    return (
      <Wrapper>
        <BenefitsSection>
          <Inner>
            <BenefitsGrid>
              {benefits.map((benefit, i) => (
                <BenefitItem key={i}>
                  <BenefitIcon icon={benefit.icon} />
                  <BenefitLabel>{__(benefit.label)}</BenefitLabel>
                </BenefitItem>
              ))}
            </BenefitsGrid>
          </Inner>
        </BenefitsSection>

        {variant === 'guest' && <FooterNewsletter />}

        <MainSection>
          <Inner>
            <NavGrid className={variant === 'school' ? 'five' : ''}>
              <Column className="primary">
                {this.renderItems(primaryItems)}
              </Column>

              {columns.map(this.renderColumn)}

              {withPayments && (
                <PaymentsBlock>
                  {PAYMENT_LOGOS.map((payment) => (
                    <PaymentTile key={payment.label} title={payment.label}>
                      <img src={payment.src} alt={payment.label} />
                    </PaymentTile>
                  ))}
                </PaymentsBlock>
              )}
            </NavGrid>

            <BottomSection>
              <CompanyBlock>
                <CompanyIdentity>
                  <CompanyName>Daffer spol. s&nbsp;r.&nbsp;o.</CompanyName>
                  <CompanyAddress>Včelárska 1, 971 01 Prievidza</CompanyAddress>
                </CompanyIdentity>
                <CompanyContacts>
                  <ContactItem href={`tel:${footerPhone.replace(/\s/g, '')}`}>
                    <ContactIcon icon="phone" />
                    {footerPhone}
                  </ContactItem>
                  <ContactItem href={`mailto:${footerMail}`}>
                    <ContactIcon icon="mail" />
                    {footerMail}
                  </ContactItem>
                </CompanyContacts>
              </CompanyBlock>

              <ScrollTopButton type="button" onClick={this.handleScrollTop}>
                <ChevronsUp aria-hidden="true">
                  <ChevronUp icon="chevron" />
                  <ChevronUp icon="chevron" />
                </ChevronsUp>
                {__('Späť na začiatok stránky')}
              </ScrollTopButton>

              <RightBlock>
                <SocialList>
                  {SOCIAL_LINKS.map((social) => (
                    <SocialItem
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={__(social.label)}
                    >
                      <SocialIcon icon={social.icon} />
                      <SocialCount>{social.count}</SocialCount>
                    </SocialItem>
                  ))}
                </SocialList>

                <SaecBadge
                  href="https://www.saec.sk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="SAEC - Bezpečný nákup"
                ></SaecBadge>
              </RightBlock>
            </BottomSection>
          </Inner>
        </MainSection>
      </Wrapper>
    );
  }
}

export default FooterNew;
